import { useAtom, useAtomValue } from 'jotai';
import { useQueryStates, parseAsString, parseAsInteger, parseAsBoolean } from 'nuqs';
import { useCallback, useMemo, useState } from 'react';

import { leadsListParamsAtom, leadsHydratedAtom } from '@/store';

export interface SearchFields {
  domain: string;
  competitors: string;
  score: string; // "min,max" format
  status: string; // comma-separated
  owner_id: string; // comma-separated
  is_ecommerce: boolean | null;
  shop: string; // comma-separated
}

export function useLeadsFilters() {
  const [params, setParams] = useAtom(leadsListParamsAtom);
  const [, setHydrated] = useAtom(leadsHydratedAtom);

  // URL state management
  const [urlState, setUrlState] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    per_page: parseAsInteger.withDefault(15),
    'filter[domain]': parseAsString,
    'filter[competitors]': parseAsString,
    'filter[score]': parseAsString,
    'filter[status]': parseAsString,
    'filter[owner_id]': parseAsString,
    'filter[is_ecommerce]': parseAsBoolean,
    'filter[shop]': parseAsString,
    'filter[collected_at]': parseAsString,
    'filter[synced_at]': parseAsString,
    'filter[created_at]': parseAsString,
    'filter[updated_at]': parseAsString,
    sort: parseAsString,
  });

  // Initialize hydrated state and sync URL with params
  useMemo(() => {
    if (!urlState.page || !urlState.per_page) return;

    const filters: Record<string, string | undefined> = {};
    if (urlState['filter[domain]']) filters.domain = urlState['filter[domain]'];
    if (urlState['filter[competitors]']) filters.competitors = urlState['filter[competitors]'];
    if (urlState['filter[score]']) filters.score = urlState['filter[score]'];
    if (urlState['filter[status]']) filters.status = urlState['filter[status]'];
    if (urlState['filter[owner_id]']) filters.owner_id = urlState['filter[owner_id]'];
    if (urlState['filter[is_ecommerce]'] !== null && urlState['filter[is_ecommerce]'] !== undefined) {
      filters.is_ecommerce = urlState['filter[is_ecommerce]'] ? 'true' : 'false';
    }
    if (urlState['filter[shop]']) filters.shop = urlState['filter[shop]'];
    if (urlState['filter[collected_at]']) filters.collected_at = urlState['filter[collected_at]'];
    if (urlState['filter[synced_at]']) filters.synced_at = urlState['filter[synced_at]'];
    if (urlState['filter[created_at]']) filters.created_at = urlState['filter[created_at]'];
    if (urlState['filter[updated_at]']) filters.updated_at = urlState['filter[updated_at]'];
    setParams({
      page: urlState.page,
      per_page: urlState.per_page,
      sort: urlState.sort || undefined,
      filters,
    });

    setHydrated(true);
  }, [
    urlState.page,
    urlState.per_page,
    urlState['filter[domain]'],
    urlState['filter[competitors]'],
    urlState['filter[score]'],
    urlState['filter[status]'],
    urlState['filter[owner_id]'],
    urlState['filter[is_ecommerce]'],
    urlState['filter[shop]'],
    urlState['filter[collected_at]'],
    urlState['filter[synced_at]'],
    urlState['filter[created_at]'],
    urlState['filter[updated_at]'],
    urlState.sort,
    setParams,
    setHydrated
  ]);

  // Search fields state
  const searchFields: SearchFields = {
    domain: params.filters.domain || '',
    competitors: params.filters.competitors || '',
    score: params.filters.score || '',
    status: params.filters.status || '',
    owner_id: params.filters.owner_id || '',
    is_ecommerce:
      params.filters.is_ecommerce === 'true' ? true : params.filters.is_ecommerce === 'false' ? false : null,
    shop: params.filters.shop || ''
  };

  // Helper to convert date range to URL format
  const dateRangeToUrl = useCallback((dateRange: { start: Date | null; end: Date | null }): string | null => {
    if (!dateRange.start && !dateRange.end) return null;
    if (dateRange.start && dateRange.end) {
      const startDate = dateRange.start.toISOString().split('T')[0];
      const endDate = dateRange.end.toISOString().split('T')[0];
      return `${startDate},${endDate}`;
    }
    if (dateRange.start) {
      return dateRange.start.toISOString().split('T')[0];
    }
    if (dateRange.end) {
      return dateRange.end.toISOString().split('T')[0];
    }
    return null;
  }, []);

  // Helper to convert URL format to date range
  const urlToDateRange = useCallback((urlValue: string | null | undefined): { start: Date | null; end: Date | null } => {
    if (!urlValue) return { start: null, end: null };
    const parts = urlValue.split(',');
    if (parts.length === 2) {
      return {
        start: parts[0] ? new Date(parts[0] + 'T00:00:00') : null,
        end: parts[1] ? new Date(parts[1] + 'T23:59:59') : null
      };
    }
    if (parts.length === 1 && parts[0]) {
      return {
        start: new Date(parts[0] + 'T00:00:00'),
        end: new Date(parts[0] + 'T23:59:59')
      };
    }
    return { start: null, end: null };
  }, []);

  // Search state - local state for input, URL state for actual search
  const [localDomainSearch, setLocalDomainSearch] = useState(urlState['filter[domain]'] || '');

  const setSearchFields = useCallback((updater: (prev: SearchFields) => SearchFields) => {
    const newFields = updater(searchFields);
    setUrlState({
      'filter[domain]': newFields.domain || null,
      'filter[competitors]': newFields.competitors || null,
      'filter[score]': newFields.score || null,
      'filter[status]': newFields.status || null,
      'filter[owner_id]': newFields.owner_id || null,
      'filter[shop]': newFields.shop || null,
      page: 1, // Reset to first page when searching
    });
  }, [searchFields, setUrlState]);

  // Domain search handler
  const handleDomainSearch = useCallback(() => {
    setUrlState({
      'filter[domain]': localDomainSearch || null,
      page: 1
    });
  }, [localDomainSearch, setUrlState]);

  // Clear search handler
  const handleClearDomainSearch = useCallback(() => {
    setLocalDomainSearch('');
    setUrlState({
      'filter[domain]': null,
      page: 1
    });
  }, [setUrlState]);

  const applyFilters = useCallback(() => {
    setUrlState({
      'filter[domain]': searchFields.domain || null,
      'filter[competitors]': searchFields.competitors || null,
      'filter[score]': searchFields.score || null,
      'filter[status]': searchFields.status || null,
      'filter[owner_id]': searchFields.owner_id || null,
      'filter[shop]': searchFields.shop || null,
      page: 1,
    });
  }, [searchFields, setUrlState]);

  const clearFilters = useCallback(() => {
    setLocalDomainSearch('');
    setUrlState({
      'filter[domain]': null,
      'filter[competitors]': null,
      'filter[score]': null,
      'filter[status]': null,
      'filter[owner_id]': null,
      'filter[is_ecommerce]': null,
      'filter[shop]': null,
      'filter[collected_at]': null,
      'filter[synced_at]': null,
      'filter[created_at]': null,
      'filter[updated_at]': null,
      sort: null, // Clear sort as well
      page: 1,
    });
  }, [setUrlState]);

  const handlePageChange = useCallback((page: number) => {
    setUrlState({ page });
  }, [setUrlState]);

  // Status filter change handler - directly updates URL (comma-separated string like competitors)
  const handleStatusFilterChange = useCallback((status: string) => {
    setUrlState({
      'filter[status]': status || null,
      page: 1, // Reset to first page when filtering
    });
  }, [setUrlState]);

  // Competitors filter change handler - directly updates URL (comma-separated string)
  const handleCompetitorsFilterChange = useCallback((competitors: string) => {
    setUrlState({
      'filter[competitors]': competitors || null,
      page: 1, // Reset to first page when filtering
    });
  }, [setUrlState]);

  // Score filter change handler
  const handleScoreFilterChange = useCallback((score: string) => {
    setUrlState({
      'filter[score]': score || null,
      page: 1,
    });
  }, [setUrlState]);

  // Owner filter change handler
  const handleOwnerFilterChange = useCallback((ownerId: string) => {
    setUrlState({
      'filter[owner_id]': ownerId || null,
      page: 1,
    });
  }, [setUrlState]);

  // E-commerce filter change handler
  const handleEcommerceFilterChange = useCallback((isEcommerce: boolean | null) => {
    setUrlState({
      'filter[is_ecommerce]': isEcommerce !== null ? isEcommerce : null,
      page: 1,
    });
  }, [setUrlState]);

  // Shop filter change handler
  const handleShopFilterChange = useCallback((shop: string) => {
    setUrlState({
      'filter[shop]': shop || null,
      page: 1,
    });
  }, [setUrlState]);

  // Date filter change handlers
  const handleCollectedDateChange = useCallback((dateRange: { start: Date | null; end: Date | null }) => {
    setUrlState({
      'filter[collected_at]': dateRangeToUrl(dateRange),
      page: 1,
    });
  }, [setUrlState, dateRangeToUrl]);

  const handleSyncedDateChange = useCallback((dateRange: { start: Date | null; end: Date | null }) => {
    setUrlState({
      'filter[synced_at]': dateRangeToUrl(dateRange),
      page: 1,
    });
  }, [setUrlState, dateRangeToUrl]);

  const handleCreatedDateChange = useCallback((dateRange: { start: Date | null; end: Date | null }) => {
    setUrlState({
      'filter[created_at]': dateRangeToUrl(dateRange),
      page: 1,
    });
  }, [setUrlState, dateRangeToUrl]);

  const handleUpdatedDateChange = useCallback((dateRange: { start: Date | null; end: Date | null }) => {
    setUrlState({
      'filter[updated_at]': dateRangeToUrl(dateRange),
      page: 1,
    });
  }, [setUrlState, dateRangeToUrl]);

  // Calculate active filters count (including sort)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (urlState['filter[domain]']) count++;
    if (urlState['filter[competitors]']) count++;
    if (urlState['filter[score]']) count++;
    if (urlState['filter[status]']) count++;
    if (urlState['filter[owner_id]']) count++;
    if (urlState['filter[is_ecommerce]'] !== null) count++;
    if (urlState['filter[shop]']) count++;
    if (urlState['filter[collected_at]']) count++;
    if (urlState['filter[synced_at]']) count++;
    if (urlState['filter[created_at]']) count++;
    if (urlState['filter[updated_at]']) count++;
    if (urlState.sort) count++; // Count sort as an active filter
    return count;
  }, [
    urlState['filter[domain]'],
    urlState['filter[competitors]'],
    urlState['filter[score]'],
    urlState['filter[status]'],
    urlState['filter[owner_id]'],
    urlState['filter[is_ecommerce]'],
    urlState['filter[shop]'],
    urlState['filter[collected_at]'],
    urlState['filter[synced_at]'],
    urlState['filter[created_at]'],
    urlState['filter[updated_at]'],
    urlState.sort
  ]);

  const hasAnyFilter = Boolean(
    urlState['filter[domain]'] ||
    urlState['filter[competitors]'] ||
    urlState['filter[score]'] ||
    urlState['filter[status]'] ||
    urlState['filter[owner_id]'] ||
    urlState['filter[is_ecommerce]'] !== null ||
    urlState['filter[shop]'] ||
    urlState['filter[collected_at]'] ||
    urlState['filter[synced_at]'] ||
    urlState['filter[created_at]'] ||
    urlState['filter[updated_at]'] ||
    urlState.sort // Include sort in filter detection
  );

  const sortParam = urlState.sort || '';
  const setSortParam = useCallback(
    (sortValue: string | null) => {
      setUrlState({
        sort: sortValue,
        page: 1,
      });
    },
    [setUrlState],
  );

  // Current filter values from URL
  const currentStatus = urlState['filter[status]'] || '';
  const currentCompetitors = urlState['filter[competitors]'] || '';
  const currentScore = urlState['filter[score]'] || '';
  const currentOwner = urlState['filter[owner_id]'] || '';
  const currentEcommerce = urlState['filter[is_ecommerce]'];
  const currentShop = urlState['filter[shop]'] || '';
  const currentCollectedDate = urlToDateRange(urlState['filter[collected_at]']);
  const currentSyncedDate = urlToDateRange(urlState['filter[synced_at]']);
  const currentCreatedDate = urlToDateRange(urlState['filter[created_at]']);
  const currentUpdatedDate = urlToDateRange(urlState['filter[updated_at]']);

  return {
    searchFields,
    setSearchFields,
    localDomainSearch,
    setLocalDomainSearch,
    applyFilters,
    clearFilters,
    handlePageChange,
    handleDomainSearch,
    handleClearDomainSearch,
    handleStatusFilterChange,
    handleCompetitorsFilterChange,
    handleScoreFilterChange,
    handleOwnerFilterChange,
    handleEcommerceFilterChange,
    handleShopFilterChange,
    handleCollectedDateChange,
    handleSyncedDateChange,
    handleCreatedDateChange,
    handleUpdatedDateChange,
    currentStatus,
    currentCompetitors,
    currentScore,
    currentOwner,
    currentEcommerce,
    currentShop,
    currentCollectedDate,
    currentSyncedDate,
    currentCreatedDate,
    currentUpdatedDate,
    hasAnyFilter,
    activeFiltersCount,
    sortParam,
    setSortParam,
  };
}

